import { inject, Injectable, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HandwerkerSucheDto, PagedResult } from '../models/handwerker-suche.model';
import { HandwerkerProfilDto, VerfuegbarkeitSlotDto } from '../models/handwerker-profil.model';

const SSR_API_BASE = 'http://api:8080';

interface SucheParams {
  gewerk?: string;
  lat?: number;
  lng?: number;
  radiusKm?: number;
  seite?: number;
  proSeite?: number;
}

function sucheStateKey(params: SucheParams) {
  return makeStateKey<PagedResult<HandwerkerSucheDto>>(
    `handwerker-suche-${JSON.stringify(params)}`,
  );
}

function profilStateKey(id: string) {
  return makeStateKey<HandwerkerProfilDto>(`handwerker-profil-${id}`);
}

@Injectable({ providedIn: 'root' })
export class HandwerkerService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);

  suche(params: SucheParams): Observable<PagedResult<HandwerkerSucheDto>> {
    const key = sucheStateKey(params);

    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(key)) {
      const cached = this.transferState.get(key, null)!;
      this.transferState.remove(key);
      return of(cached);
    }

    const httpParams = this.buildParams(params);
    const base = isPlatformBrowser(this.platformId) ? '' : SSR_API_BASE;
    const url = `${base}/api/handwerker/suche`;

    return this.http.get<PagedResult<HandwerkerSucheDto>>(url, { params: httpParams }).pipe(
      tap((data) => {
        if (!isPlatformBrowser(this.platformId)) {
          this.transferState.set(key, data);
        }
      }),
    );
  }

  getProfil(id: string): Observable<HandwerkerProfilDto> {
    const key = profilStateKey(id);

    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(key)) {
      const cached = this.transferState.get(key, null)!;
      this.transferState.remove(key);
      return of(cached);
    }

    const base = isPlatformBrowser(this.platformId) ? '' : SSR_API_BASE;
    const url = `${base}/api/handwerker/${id}`;

    return this.http.get<HandwerkerProfilDto>(url).pipe(
      tap((data) => {
        if (!isPlatformBrowser(this.platformId)) {
          this.transferState.set(key, data);
        }
      }),
    );
  }

  getVerfuegbarkeit(
    id: string,
    von: Date,
    bis: Date,
  ): Observable<VerfuegbarkeitSlotDto[]> {
    const params = new HttpParams()
      .set('von', von.toISOString())
      .set('bis', bis.toISOString());
    return this.http.get<VerfuegbarkeitSlotDto[]>(`/api/handwerker/${id}/verfuegbarkeit`, {
      params,
    });
  }

  private buildParams(p: SucheParams): HttpParams {
    let params = new HttpParams();
    if (p.gewerk) params = params.set('gewerk', p.gewerk);
    if (p.lat != null) params = params.set('lat', p.lat);
    if (p.lng != null) params = params.set('lng', p.lng);
    if (p.radiusKm != null) params = params.set('radiusKm', p.radiusKm);
    if (p.seite != null) params = params.set('seite', p.seite);
    if (p.proSeite != null) params = params.set('proSeite', p.proSeite);
    return params;
  }
}
